import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { QrCode as QrCodeIcon, LogOut, MessageSquare, Trash2, Download, Crown, AlertTriangle, Package, Camera, Phone } from 'lucide-react';
import AppTopHeader from '../components/AppTopHeader';
import BrandedQRCode from '../components/BrandedQRCode';
import UpgradeModal from '../components/UpgradeModal';
import { downloadBrandedCard } from '../lib/qr-print-utils';
import { API } from '../App';

const Dashboard = () => {
  const navigate = useNavigate();
  const RENEWAL_WARNING_DAYS = 14;
  const [user, setUser] = useState(null);
  const [cars, setCars] = useState([]);
  const [messages, setMessages] = useState([]);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [calls, setCalls] = useState([]);
  const [callsLoading, setCallsLoading] = useState(true);
  const [expandedHistoryByQR, setExpandedHistoryByQR] = useState({});
  const [historyTabByQR, setHistoryTabByQR] = useState({});
  const [updatingCommunication, setUpdatingCommunication] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showQR, setShowQR] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(null); // QR code ID to upgrade
  const [pricing, setPricing] = useState({ finalPricePaise: 34900, durationDays: 365 });

  const formatPrice = (paise) => Math.round(paise / 100);

  useEffect(() => {
    const loadPricing = async () => {
      try {
        const response = await axios.get(`${API}/settings/user`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        setPricing({
          finalPricePaise: response.data.final_price_paise,
          durationDays: response.data.subscription_duration_days
        });
      } catch (error) {
        // Keep defaults
      }
    };

    loadPricing();
  }, []);

  useEffect(() => {
    loadUserData();
    loadCars();
    loadMessages();
    loadOrders();
    loadCalls();
  }, []);

  const loadUserData = async () => {
    try {
      const response = await axios.get(`${API}/user/profile`);
      setUser(response.data.user);
    } catch (error) {
      console.error('Failed to load user data:', error);
    }
  };

  const loadCars = async () => {
    try {
      const response = await axios.get(`${API}/cars`);
      setCars(response.data.cars);
    } catch (error) {
      toast.error('Failed to load cars');
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async () => {
    try {
      const response = await axios.get(`${API}/messages`);
      setMessages(response.data.messages);
    } catch (error) {
      console.error('Failed to load messages');
    }
  };

  const loadOrders = async () => {
    try {
      const response = await axios.get(`${API}/payment/qr-orders`);
      setOrders(response.data.orders || []);
    } catch (error) {
      // Ignore on dashboard if no orders API data yet
    } finally {
      setOrdersLoading(false);
    }
  };

  const loadCalls = async () => {
    try {
      const response = await axios.get(`${API}/calls`);
      setCalls(response.data.calls || []);
    } catch (error) {
      // Non-fatal
    } finally {
      setCallsLoading(false);
    }
  };

  const handleDeleteCar = async (carId) => {
    if (!window.confirm('Are you sure? This will delete the car and its QR code.')) return;
    
    try {
      await axios.delete(`${API}/cars/${carId}`);
      toast.success('Car deleted');
      loadCars();
    } catch (error) {
      toast.error('Failed to delete car');
    }
  };

  const setAvailability = async (isAvailable, showToast = true) => {
    try {
      await axios.put(`${API}/user/availability`, { is_available: isAvailable });
      setUser({ ...user, is_available: isAvailable });
      if (showToast) {
        toast.success(isAvailable ? 'You are now available' : 'You are now unavailable');
      }
      return true;
    } catch (error) {
      toast.error('Failed to update availability');
      return false;
    }
  };

  const updateCommunicationPreferences = async (nextPreferences, successMessage) => {
    try {
      setUpdatingCommunication(true);
      const response = await axios.put(`${API}/user/communication-preferences`, nextPreferences);
      setUser((previous) => ({
        ...previous,
        calls_enabled: response.data.calls_enabled,
        messages_enabled: response.data.messages_enabled,
        is_available: response.data.is_available
      }));
      toast.success(successMessage);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update communication controls');
    } finally {
      setUpdatingCommunication(false);
    }
  };

  const updateQRCommunicationPreferences = async (qrId, nextPrefs, successMessage) => {
    try {
      const response = await axios.put(`${API}/qr/${qrId}/communication-preferences`, nextPrefs);
      setCars((prev) =>
        prev.map((c) =>
          c.qr_code === qrId
            ? { ...c, calls_enabled: response.data.calls_enabled, messages_enabled: response.data.messages_enabled }
            : c
        )
      );
      toast.success(successMessage);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update vehicle controls');
    }
  };

  const callsEnabled = user?.calls_enabled !== false;
  const messagesEnabled = user?.messages_enabled !== false;

  const toggleVehicleHistory = (qrCode) => {
    setExpandedHistoryByQR((prev) => ({
      ...prev,
      [qrCode]: !prev[qrCode]
    }));
  };

  const setVehicleHistoryTab = (qrCode, tab) => {
    setHistoryTabByQR((prev) => ({
      ...prev,
      [qrCode]: tab
    }));
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const downloadQR = async (qrId, qrCode) => {
    try {
      await downloadBrandedCard(qrId || qrCode);
      toast.success('Sticker downloaded');
    } catch (error) {
      toast.error('Failed to download sticker');
    }
  };

  const getQRUrl = (qrCode) => {
    return `${window.location.origin}/scan/${qrCode}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <AppTopHeader
        sticky
        backLabel="Back"
        rightSlot={(
          <button
            onClick={handleLogout}
            className="p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition"
            data-testid="logout-btn"
            title="Logout"
          >
            <LogOut className="w-5 h-5" />
          </button>
        )}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Communication Controls */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-6">
          <div>
            <h2 className="text-xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Barlow' }}>
              Welcome Back!
            </h2>
            <p className="text-base font-semibold text-slate-700">{user?.phone}</p>
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3 bg-slate-50">
              <div>
                <p className="text-sm font-semibold text-slate-900">Calls</p>
                <p className="text-xs text-slate-500">Allow scanner to call you</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={callsEnabled}
                onClick={async () => {
                  const nextCallsEnabled = !callsEnabled;
                  const nextAvailability = nextCallsEnabled || messagesEnabled;
                  const availabilityUpdated = await setAvailability(nextAvailability, false);
                  if (!availabilityUpdated) return;
                  await updateCommunicationPreferences(
                    { calls_enabled: nextCallsEnabled },
                    nextCallsEnabled
                      ? 'Calls enabled'
                      : nextAvailability
                        ? 'Calls disabled'
                        : 'Calls disabled. You are now unavailable'
                  );
                }}
                disabled={updatingCommunication}
                className={`relative inline-flex min-h-0 min-w-0 h-6 w-12 items-center rounded-full transition-colors ${
                  callsEnabled ? 'bg-emerald-500' : 'bg-slate-300'
                } ${updatingCommunication ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                <span
                  className={`absolute left-0.5 top-0.5 h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                    callsEnabled ? 'translate-x-6' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3 bg-slate-50">
              <div>
                <p className="text-sm font-semibold text-slate-900">Messages</p>
                <p className="text-xs text-slate-500">Allow scanner to send quick messages</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={messagesEnabled}
                onClick={async () => {
                  const nextMessagesEnabled = !messagesEnabled;
                  const nextAvailability = callsEnabled || nextMessagesEnabled;
                  const availabilityUpdated = await setAvailability(nextAvailability, false);
                  if (!availabilityUpdated) return;
                  await updateCommunicationPreferences(
                    { messages_enabled: nextMessagesEnabled },
                    nextMessagesEnabled
                      ? 'Messages enabled'
                      : nextAvailability
                        ? 'Messages disabled'
                        : 'Messages disabled. You are now unavailable'
                  );
                }}
                disabled={updatingCommunication}
                className={`relative inline-flex min-h-0 min-w-0 h-6 w-12 items-center rounded-full transition-colors ${
                  messagesEnabled ? 'bg-emerald-500' : 'bg-slate-300'
                } ${updatingCommunication ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                <span
                  className={`absolute left-0.5 top-0.5 h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                    messagesEnabled ? 'translate-x-6' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Barlow' }}>
              My Cars
            </h2>
          </div>

          <div className="space-y-4">
            {cars.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
                <QrCodeIcon className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-600">No cars linked yet. Scan your QR to claim your car.</p>
              </div>
            ) : (
              cars.map((car) => {
                const isDisabledByAdmin = car.subscription_status === 'disabled_by_admin' || car.subscription_status === 'deleted_by_admin' || car.subscription_status === 'deleted_by_owner';
                const isExpired = !isDisabledByAdmin && (car.is_expired || car.subscription_status === 'expired' || car.is_active === false);
                const expiryDate = car.subscription_expiry ? new Date(car.subscription_expiry) : null;
                const daysToExpiry = expiryDate ? Math.ceil((expiryDate - new Date()) / (1000 * 60 * 60 * 24)) : null;
                const isExpiringSoon = !isDisabledByAdmin && daysToExpiry !== null && daysToExpiry >= 0 && daysToExpiry <= RENEWAL_WARNING_DAYS && !isExpired;
                const carMessages = car.qr_code ? messages.filter((msg) => msg.qr_code === car.qr_code) : [];
                const carCalls = car.qr_code ? calls.filter((call) => call.qr_code === car.qr_code) : [];
                const isHistoryVisible = car.qr_code ? !!expandedHistoryByQR[car.qr_code] : false;
                const activeHistoryTab = car.qr_code ? (historyTabByQR[car.qr_code] || 'messages') : 'messages';

                return (
                  <div
                    key={car.id}
                    className="bg-white rounded-xl border border-slate-200 p-4 hover:border-blue-200 transition-colors"
                    data-testid={`car-card-${car.id}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <h3 className="text-lg font-semibold text-slate-900">
                          {car.nickname || 'My Car'}
                        </h3>
                        {isDisabledByAdmin ? (
                          <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                            {car.subscription_status === 'deleted_by_admin' ? 'Deleted by Admin' :
                             car.subscription_status === 'deleted_by_owner' ? 'Deleted' :
                             'Disabled by Admin'}
                          </span>
                        ) : isExpired ? (
                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                            Expired
                          </span>
                        ) : (
                          <span className="flex items-center text-xs bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full">
                            <Crown className="w-3 h-3 mr-1" />
                            Active
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => {
                            if (isDisabledByAdmin) return;
                            if (isExpired) {
                              setShowUpgrade(car.qr_code_id || car.id);
                              return;
                            }
                            setShowQR(car);
                          }}
                          className={`p-2 rounded-lg ${
                            isDisabledByAdmin
                              ? 'text-slate-300 cursor-not-allowed'
                              : isExpired
                              ? 'text-amber-600 hover:bg-amber-50'
                              : 'hover:bg-blue-50 text-blue-800'
                          }`}
                          data-testid={`view-qr-btn-${car.id}`}
                        >
                          <QrCodeIcon className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteCar(car.id)}
                          className="p-2 rounded-lg hover:bg-red-50 text-red-600"
                          data-testid={`delete-car-btn-${car.id}`}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>

                    {isExpired && (
                      <div className="mb-3 p-3 bg-amber-50 border border-amber-100 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <AlertTriangle className="w-4 h-4 text-amber-600" />
                            <span className="text-sm text-amber-800">
                              Renewal required to reactivate this QR code
                            </span>
                          </div>
                          <button
                            onClick={() => setShowUpgrade(car.qr_code_id || car.id)}
                            className="text-xs font-medium text-amber-700 hover:text-amber-900 underline"
                            data-testid={`upgrade-btn-${car.id}`}
                          >
                            Renew ₹{formatPrice(pricing.finalPricePaise)}/year
                          </button>
                        </div>
                      </div>
                    )}

                    {isDisabledByAdmin && (
                      <div className="mb-3 p-3 bg-red-50 border border-red-100 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <AlertTriangle className="w-4 h-4 text-red-600" />
                          <span className="text-sm text-red-800">
                            {car.subscription_status === 'deleted_by_admin'
                              ? 'This QR has been deleted by admin. Contact support for assistance.'
                              : car.subscription_status === 'deleted_by_owner'
                              ? 'This vehicle has been removed.'
                              : 'This QR has been disabled by admin. Contact support for assistance.'}
                          </span>
                        </div>
                      </div>
                    )}

                    {isExpiringSoon && (
                      <div className="mb-3 p-3 bg-amber-50 border border-amber-100 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <AlertTriangle className="w-4 h-4 text-amber-600" />
                            <span className="text-sm text-amber-800">
                              Expires in {daysToExpiry} day{daysToExpiry === 1 ? '' : 's'}
                            </span>
                          </div>
                          <button
                            onClick={() => setShowUpgrade(car.qr_code_id || car.id)}
                            className="text-xs font-medium text-amber-700 hover:text-amber-900 underline"
                            data-testid={`renew-btn-${car.id}`}
                          >
                            Renew ₹{formatPrice(pricing.finalPricePaise)}/year
                          </button>
                        </div>
                      </div>
                    )}

                    {car.subscription_expiry && !isExpired && !isDisabledByAdmin && (
                      <div className="mb-3 p-3 bg-emerald-50 border border-emerald-100 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <Crown className="w-4 h-4 text-emerald-600" />
                          <span className="text-sm text-emerald-800">
                            Active until {new Date(car.subscription_expiry).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    )}

                    {car.subscription_expiry && isExpired && (
                      <div className="mb-3 p-3 bg-amber-50 border border-amber-100 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <AlertTriangle className="w-4 h-4 text-amber-600" />
                          <span className="text-sm text-amber-800">
                            Expired on {new Date(car.subscription_expiry).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    )}

                    {car.qr_code && !isExpired && (
                      <div className="text-xs text-slate-500 font-mono bg-slate-50 p-2 rounded">
                        QR: {car.qr_code.substring(0, 20)}...
                      </div>
                    )}
                    {isExpired && (
                      <div className="text-xs text-amber-700 bg-amber-50 border border-amber-100 p-2 rounded">
                        Renew ₹{formatPrice(pricing.finalPricePaise)}/year to reactivate and view this QR code
                      </div>
                    )}

                    {car.qr_code && !isExpired && (
                      <div className="mt-3 pt-3 border-t border-slate-100 space-y-2.5">
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">This Vehicle</p>

                        {!isDisabledByAdmin && (
                        <div className="grid grid-cols-2 gap-3">
                          <div className="flex items-center justify-between gap-1.5 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <Phone className="w-3.5 h-3.5 text-slate-500" />
                              <span className="text-xs font-medium text-slate-600">Calls</span>
                            </div>
                            <button
                              type="button"
                              role="switch"
                              aria-checked={car.calls_enabled !== false}
                              onClick={() => {
                                const next = car.calls_enabled === false;
                                updateQRCommunicationPreferences(
                                  car.qr_code,
                                  { calls_enabled: next },
                                  next ? 'Calls enabled for this vehicle' : 'Calls disabled for this vehicle'
                                );
                              }}
                              className={`relative inline-flex min-h-0 min-w-0 shrink-0 h-6 w-12 items-center rounded-full transition-colors ${
                                car.calls_enabled !== false ? 'bg-emerald-500' : 'bg-slate-300'
                              }`}
                            >
                              <span className={`absolute left-0.5 top-0.5 h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                                car.calls_enabled !== false ? 'translate-x-6' : 'translate-x-0'
                              }`} />
                            </button>
                          </div>
                          <div className="flex items-center justify-between gap-1.5 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <MessageSquare className="w-3.5 h-3.5 text-slate-500" />
                              <span className="text-xs font-medium text-slate-600">Messages</span>
                            </div>
                            <button
                              type="button"
                              role="switch"
                              aria-checked={car.messages_enabled !== false}
                              onClick={() => {
                                const next = car.messages_enabled === false;
                                updateQRCommunicationPreferences(
                                  car.qr_code,
                                  { messages_enabled: next },
                                  next ? 'Messages enabled for this vehicle' : 'Messages disabled for this vehicle'
                                );
                              }}
                              className={`relative inline-flex min-h-0 min-w-0 shrink-0 h-6 w-12 items-center rounded-full transition-colors ${
                                car.messages_enabled !== false ? 'bg-emerald-500' : 'bg-slate-300'
                              }`}
                            >
                              <span className={`absolute left-0.5 top-0.5 h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                                car.messages_enabled !== false ? 'translate-x-6' : 'translate-x-0'
                              }`} />
                            </button>
                          </div>
                        </div>
                        )}

                        <button
                          onClick={() => toggleVehicleHistory(car.qr_code)}
                          className="min-h-0 min-w-0 text-sm font-semibold text-black underline underline-offset-2 bg-transparent p-0 border-0 hover:text-gray-800"
                        >
                          {isHistoryVisible ? 'Hide History' : 'View History'}
                        </button>

                        {isHistoryVisible && (
                          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-3">
                            <div className="flex items-center gap-1.5">
                              <button
                                onClick={() => setVehicleHistoryTab(car.qr_code, 'messages')}
                                className={`min-h-0 min-w-0 px-2 py-0.5 text-[10px] leading-4 font-semibold rounded ${
                                  activeHistoryTab === 'messages'
                                    ? 'bg-[#0DB504] text-white'
                                    : 'bg-white border border-slate-200 text-slate-600'
                                }`}
                              >
                                Messages
                              </button>
                              <button
                                onClick={() => setVehicleHistoryTab(car.qr_code, 'calls')}
                                className={`min-h-0 min-w-0 px-2 py-0.5 text-[10px] leading-4 font-semibold rounded ${
                                  activeHistoryTab === 'calls'
                                    ? 'bg-[#0DB504] text-white'
                                    : 'bg-white border border-slate-200 text-slate-600'
                                }`}
                              >
                                Calls
                              </button>
                            </div>

                            {activeHistoryTab === 'messages' ? (
                              <div className="space-y-2">
                                {carMessages.length === 0 ? (
                                  <p className="text-xs text-slate-500">No messages for this QR yet.</p>
                                ) : (
                                  carMessages.slice(0, 20).map((msg) => (
                                    <div key={msg.id} className="bg-white border border-slate-200 rounded-md p-2.5">
                                      <div className="flex items-start justify-between mb-1.5">
                                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-blue-100 text-blue-800">
                                          {msg.message_type.toUpperCase()}
                                        </span>
                                        <span className="text-[10px] text-slate-500">{new Date(msg.created_at).toLocaleString()}</span>
                                      </div>
                                      <p className="text-xs text-slate-900">{msg.content}</p>
                                    </div>
                                  ))
                                )}
                              </div>
                            ) : (
                              <div className="space-y-2">
                                {callsLoading ? (
                                  <p className="text-xs text-slate-500">Loading calls...</p>
                                ) : carCalls.length === 0 ? (
                                  <p className="text-xs text-slate-500">No calls for this QR yet.</p>
                                ) : (
                                  carCalls.slice(0, 20).map((call) => {
                                    const status = call.status || 'initiated';
                                    return (
                                      <div key={call.id} className="bg-white border border-slate-200 rounded-md p-2.5">
                                        <div className="flex items-start justify-between mb-1">
                                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-700">
                                            {status.replace(/_/g, ' ').toUpperCase()}
                                          </span>
                                          <span className="text-[10px] text-slate-500">{new Date(call.created_at).toLocaleString()}</span>
                                        </div>
                                        <p className="text-xs font-semibold text-slate-900">+91 {call.scanner_phone}</p>
                                      </div>
                                    );
                                  })
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="mt-6">
          <div className="grid grid-cols-2 gap-2 mb-4 max-w-md">
            <button
              onClick={() => navigate('/activate-qr')}
              className="bg-[#0DB504] text-white rounded-lg py-2 px-3 hover:bg-[#0a9e03] transition-all shadow-sm flex items-center justify-center gap-1.5 font-semibold text-xs sm:text-sm"
            >
              <Camera className="w-4 h-4" />
              Activate QR
            </button>
            <button
              onClick={() => navigate('/buy-qr')}
              className="bg-white border-2 border-[#0DB504] text-[#0DB504] font-semibold rounded-lg py-2 px-3 hover:bg-green-50 transition-all text-xs sm:text-sm"
            >
              Buy QR Codes
            </button>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Package className="w-5 h-5 text-[#0DB504]" />
                <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Barlow' }}>
                  My QR Orders
                </h2>
              </div>
            </div>

            {ordersLoading ? (
              <p className="text-sm text-slate-500">Loading orders...</p>
            ) : orders.length === 0 ? (
              <p className="text-sm text-slate-500">No QR orders yet.</p>
            ) : (
              <div className="space-y-3 max-h-80 overflow-auto pr-1">
                {orders.slice(0, 20).map((order) => (
                  <div key={order.order_id} className="border border-slate-200 rounded-lg p-4 bg-gradient-to-br from-slate-50 to-slate-25 hover:shadow-md transition-all">
                    <div className="flex flex-col gap-3">
                      {/* Order ID and Date */}
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pb-3 border-b border-slate-200">
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Order ID</p>
                          <p className="text-sm font-mono font-bold text-slate-900">{order.display_order_id || order.order_id}</p>
                        </div>
                        <p className="text-xs text-slate-500">{order.created_at ? new Date(order.created_at).toLocaleString() : '-'}</p>
                      </div>

                      {/* Order Details Grid */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wide mb-1 font-semibold">Quantity</p>
                          <p className="text-lg font-bold text-slate-900">{order.quantity || 1}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wide mb-1 font-semibold">Amount</p>
                          <p className="text-lg font-bold text-slate-900">₹{Math.round((order.amount_paise || 0) / 100)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wide mb-1 font-semibold">Payment</p>
                          <p className="text-sm font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded w-fit">{order.status || 'created'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wide mb-1 font-semibold">Fulfillment</p>
                          <p className="text-sm font-medium text-blue-700 bg-blue-50 px-2 py-1 rounded w-fit">{(order.fulfillment_status || 'pending').replace(/_/g, ' ')}</p>
                        </div>
                      </div>

                      {/* Tracking Info */}
                      {(order.tracking_number || order.tracking_url) && (
                        <div className="pt-2 border-t border-slate-200">
                          {order.tracking_number && <p className="text-xs text-slate-600 mb-1"><span className="font-semibold">Tracking:</span> {order.tracking_number}</p>}
                          {order.tracking_url && (
                            <a
                              href={order.tracking_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-black hover:text-gray-800 underline"
                            >
                              View shipment tracking →
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
          </div>
        </div>
      </div>


      {/* QR Code Modal */}
      {showQR && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-slate-50 rounded-2xl shadow-2xl max-w-lg w-full my-8">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Barlow' }}>
                Your sideo QR Code
              </h3>
              <button
                onClick={() => setShowQR(null)}
                className="p-2 rounded-lg hover:bg-slate-200 text-slate-600"
                data-testid="close-qr-modal-btn"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              <div className="max-w-xs mx-auto">
              <BrandedQRCode 
                qrId={showQR.qr_code}
                qrCode={showQR.qr_code}
                carNickname={showQR.nickname}
              />
              </div>
              <div className="mt-6 flex space-x-3">
                <button
                  onClick={() => downloadQR(showQR.qr_code, showQR.qr_code, showQR.nickname)}
                  className="flex-1 btn-primary flex items-center justify-center"
                  data-testid="download-qr-btn"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Print QR Code
                </button>
              </div>
              <p className="text-xs text-slate-500 text-center mt-4">
                Print and place on your car dashboard for easy contact
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={!!showUpgrade}
        onClose={() => setShowUpgrade(null)}
        qrCodeId={showUpgrade}
        carNickname={cars.find(c => c.qr_code_id === showUpgrade || c.id === showUpgrade)?.nickname}
        onSuccess={() => {
          loadCars();
          loadUserData();
        }}
      />

    </div>
  );
};

export default Dashboard;
